<template>
  <WebinarReplayPlayer
    :open="Boolean(replayEvent)"
    :event="replayEvent"
    @close="closeReplay"
  />

  <WebinarRegistrantsModal
    :open="Boolean(registrantsEvent)"
    :event="registrantsEvent"
    @close="registrantsEvent = null"
  />

  <WebinarEventWizard
    v-if="activeView === 'event-wizard'"
    :event="editingEvent"
    :products="availableProducts"
    :selected-product-id="selectedProductId"
    :saving="webinarStore.saving"
    :save-error="saveError"
    :prototype-mode="Boolean(selectedProduct?.isPrototype)"
    @close="returnToHub"
    @save="saveWebinarEvent"
  />

  <WebinarStreamStudio
    v-else-if="activeView === 'studio'"
    :event="studioEvent"
    :product="selectedProduct"
    @close="returnToHub"
    @edit-event="openEditWizard(studioEvent)"
    @broadcast-ended="activeTab = 'Completed'"
  />

  <main v-else class="mx-auto w-full max-w-[1380px] pb-14">
    <Transition name="clone-toast">
      <div
        v-if="cloneSuccessMessage"
        class="fixed right-6 top-6 z-[70] flex max-w-sm items-center gap-3 rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-emerald-800 shadow-2xl shadow-emerald-900/15"
        role="status"
        aria-live="polite"
      >
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircleIcon class="h-6 w-6 text-emerald-600" />
        </span>
        <div>
          <p class="text-sm font-bold">Webinar cloned</p>
          <p class="mt-0.5 text-xs leading-5 text-emerald-700">{{ cloneSuccessMessage }}</p>
        </div>
      </div>
    </Transition>

    <div
      v-if="previewMode && !hasRealWebinarProduct"
      class="mb-6 flex flex-col gap-3 rounded-2xl border border-violet-200 bg-violet-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex items-start gap-3">
        <SparklesIcon class="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
        <div>
          <p class="text-sm font-semibold text-violet-900">Frontend preview mode</p>
          <p class="mt-0.5 text-sm text-violet-700">
            This sample Webinar product and its events only exist on this page. Nothing is being saved.
          </p>
        </div>
      </div>
      <button
        type="button"
        class="shrink-0 text-sm font-semibold text-violet-700 hover:text-violet-900"
        @click="previewMode = false"
      >
        Return to empty state
      </button>
    </div>

    <template v-if="isLoadingProducts">
      <header class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" aria-label="Loading webinar products">
        <div class="w-full max-w-2xl">
          <div class="stream-skeleton h-4 w-32 rounded-full" />
          <div class="stream-skeleton mt-3 h-9 w-64 rounded-lg" />
          <div class="stream-skeleton mt-3 h-4 w-full max-w-xl rounded" />
        </div>
        <div class="stream-skeleton h-12 w-36 rounded-xl" />
      </header>

      <section class="mt-8 grid gap-4 sm:grid-cols-3">
        <div v-for="metric in 3" :key="`product-metric-skeleton-${metric}`" class="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="stream-skeleton h-4 w-28 rounded" />
            <div class="stream-skeleton h-5 w-5 rounded-md" />
          </div>
          <div class="stream-skeleton mt-4 h-9 w-16 rounded-lg" />
          <div class="stream-skeleton mt-3 h-3 w-40 max-w-full rounded" />
        </div>
      </section>

      <section class="mt-7 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="flex flex-col gap-5 border-b border-zinc-200 p-5 lg:flex-row lg:items-end lg:justify-between">
          <div class="w-full max-w-sm">
            <div class="stream-skeleton h-3 w-28 rounded" />
            <div class="stream-skeleton mt-3 h-12 w-full rounded-xl" />
          </div>
          <div class="flex gap-2">
            <div v-for="tab in 5" :key="`product-tab-skeleton-${tab}`" class="stream-skeleton h-9 w-24 rounded-lg" />
          </div>
        </div>
        <div class="divide-y divide-zinc-100 px-5">
          <div v-for="row in 3" :key="`product-row-skeleton-${row}`" class="grid gap-5 py-5 lg:grid-cols-[190px_minmax(0,1fr)_auto] lg:items-center">
            <div class="stream-skeleton aspect-video rounded-xl" />
            <div>
              <div class="stream-skeleton h-3 w-44 rounded" />
              <div class="stream-skeleton mt-3 h-6 w-full max-w-md rounded" />
              <div class="mt-4 flex gap-3">
                <div class="stream-skeleton h-4 w-28 rounded" />
                <div class="stream-skeleton h-4 w-24 rounded" />
              </div>
            </div>
            <div class="flex gap-2">
              <div class="stream-skeleton h-10 w-20 rounded-xl" />
              <div class="stream-skeleton h-10 w-28 rounded-xl" />
            </div>
          </div>
        </div>
      </section>
    </template>

    <section
      v-else-if="!hasWebinarProduct"
      class="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm"
    >
      <div class="grid min-h-[610px] lg:grid-cols-[1.05fr_0.95fr]">
        <div class="flex flex-col justify-center px-7 py-12 sm:px-12 lg:px-16">
          <span class="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-blue-700">
            <VideoCameraIcon class="h-4 w-4" />
            Engage · Stream
          </span>
          <h1 class="max-w-xl text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">
            Add a Webinar product before creating your first live event.
          </h1>
          <p class="mt-5 max-w-xl text-base leading-7 text-zinc-600">
            Webinar products control where events appear in your member area. Add and design the product in Theme Editor, then return here to schedule events and run your broadcasts.
          </p>

          <div class="mt-8 flex flex-col gap-3 sm:flex-row">
            <router-link
              to="/creator/theme-editor"
              class="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2478ff] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600"
            >
              Open Theme Editor
              <ArrowUpRightIcon class="h-4 w-4" />
            </router-link>
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-300 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50"
              @click="previewMode = true"
            >
              <EyeIcon class="h-4 w-4" />
              Preview interface
            </button>
          </div>

          <div class="mt-10 grid gap-4 border-t border-zinc-100 pt-8 sm:grid-cols-3">
            <div v-for="item in emptyStateSteps" :key="item.label" class="flex gap-3 sm:block">
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-950 text-xs font-bold text-white">
                {{ item.step }}
              </span>
              <div class="sm:mt-3">
                <p class="text-sm font-semibold text-zinc-900">{{ item.label }}</p>
                <p class="mt-1 text-xs leading-5 text-zinc-500">{{ item.copy }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="relative flex min-h-[420px] items-center justify-center overflow-hidden bg-gradient-to-br from-[#ecf4ff] via-white to-violet-100 p-10">
          <div class="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-blue-300/30 blur-3xl"></div>
          <div class="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-violet-300/30 blur-3xl"></div>
          <div class="relative w-full max-w-[500px]">
            <div class="rounded-[28px] border border-white/80 bg-white/80 p-4 shadow-2xl shadow-blue-900/10 backdrop-blur">
              <div class="relative aspect-video overflow-hidden rounded-2xl bg-zinc-950">
                <img :src="webinarImage" alt="Webinar product preview" class="h-full w-full object-cover opacity-65" />
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div class="absolute left-5 top-5 rounded-full bg-red-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Live</div>
                <div class="absolute bottom-5 left-5 right-5">
                  <p class="text-lg font-semibold text-white">Your live event experience</p>
                  <p class="mt-1 text-sm text-zinc-300">Designed in Theme Editor. Run from Stream.</p>
                </div>
              </div>
              <div class="flex items-center justify-between px-2 pb-1 pt-4">
                <div class="flex items-center gap-3">
                  <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                    <CalendarDaysIcon class="h-5 w-5 text-blue-700" />
                  </div>
                  <div>
                    <div class="h-2.5 w-28 rounded bg-zinc-200"></div>
                    <div class="mt-2 h-2 w-20 rounded bg-zinc-100"></div>
                  </div>
                </div>
                <div class="h-8 w-24 rounded-lg bg-zinc-950"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <template v-else>
      <header class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div class="flex items-center gap-2 text-sm font-semibold text-blue-600">
            <SignalIcon class="h-4 w-4" />
            Engage · Stream
          </div>
          <h1 class="mt-2 text-3xl font-bold tracking-tight text-zinc-950">Live webinars</h1>
          <p class="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
            Schedule events, prepare your broadcast, and manage every live webinar from one place.
          </p>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2478ff] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600"
          @click="openCreateWizard"
        >
          <PlusIcon class="h-5 w-5" />
          Create event
        </button>
      </header>

      <section class="mt-8 grid gap-4 sm:grid-cols-3">
        <template v-if="isLoadingEvents">
          <div
            v-for="metric in 3"
            :key="`event-metric-skeleton-${metric}`"
            class="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm"
          >
            <div class="flex items-center justify-between">
              <div class="stream-skeleton h-4 w-28 rounded" />
              <div class="stream-skeleton h-5 w-5 rounded-md" />
            </div>
            <div class="stream-skeleton mt-4 h-9 w-16 rounded-lg" />
            <div class="stream-skeleton mt-3 h-3 w-40 max-w-full rounded" />
          </div>
        </template>
        <template v-else>
          <div v-for="metric in metrics" :key="metric.label" class="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <div class="flex items-center justify-between">
              <p class="text-sm font-medium text-zinc-500">{{ metric.label }}</p>
              <component :is="metric.icon" class="h-5 w-5" :class="metric.iconClass" />
            </div>
            <p class="mt-3 text-3xl font-bold tracking-tight text-zinc-950">{{ metric.value }}</p>
            <p class="mt-1 text-xs text-zinc-500">{{ metric.note }}</p>
          </div>
        </template>
      </section>

      <section class="mt-7 rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="flex flex-col gap-5 border-b border-zinc-200 p-5 lg:flex-row lg:items-end lg:justify-between">
          <div class="w-full max-w-sm">
            <label class="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Webinar product</label>
            <div class="relative mt-2">
              <select
                v-model="selectedProductId"
                class="block w-full appearance-none rounded-xl border border-zinc-300 bg-white py-3 pl-4 pr-10 text-sm font-semibold text-zinc-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option v-for="product in availableProducts" :key="product.id" :value="product.id">
                  {{ product.name }}
                </option>
              </select>
              <ChevronDownIcon class="pointer-events-none absolute right-3 top-3.5 h-5 w-5 text-zinc-400" />
            </div>
          </div>

          <div v-if="isLoadingEvents" class="flex flex-wrap gap-2" aria-label="Loading event filters">
            <div v-for="tab in 5" :key="`event-tab-skeleton-${tab}`" class="stream-skeleton h-9 w-24 rounded-lg" />
          </div>
          <div v-else class="flex flex-wrap gap-2">
            <button
              v-for="tab in eventTabs"
              :key="tab.id"
              type="button"
              class="rounded-lg px-4 py-2 text-sm font-semibold transition"
              :class="activeTab === tab.id ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
              <span class="ml-1 opacity-70">{{ tab.count }}</span>
            </button>
          </div>
        </div>

        <div v-if="eventLoadError" class="border-b border-red-100 bg-red-50 px-5 py-3 text-sm text-red-700">
          {{ eventLoadError }}
        </div>

        <div v-if="isLoadingEvents" class="divide-y divide-zinc-100 px-5" aria-label="Loading webinar events" aria-live="polite">
          <div
            v-for="row in 3"
            :key="`event-row-skeleton-${row}`"
            class="grid gap-5 py-5 lg:grid-cols-[190px_minmax(0,1fr)_auto] lg:items-center"
          >
            <div class="stream-skeleton aspect-video rounded-xl" />
            <div>
              <div class="flex gap-2">
                <div class="stream-skeleton h-3 w-28 rounded" />
                <div class="stream-skeleton h-3 w-36 rounded" />
              </div>
              <div class="stream-skeleton mt-3 h-6 w-full max-w-md rounded" />
              <div class="mt-4 flex flex-wrap gap-3">
                <div class="stream-skeleton h-4 w-32 rounded" />
                <div class="stream-skeleton h-4 w-28 rounded" />
                <div class="stream-skeleton h-4 w-24 rounded" />
              </div>
            </div>
            <div class="flex gap-2 lg:justify-end">
              <div class="stream-skeleton h-10 w-20 rounded-xl" />
              <div class="stream-skeleton h-10 w-28 rounded-xl" />
              <div class="stream-skeleton h-10 w-10 rounded-xl" />
            </div>
          </div>
        </div>

        <TransitionGroup
          v-else-if="filteredEvents.length"
          name="webinar-event-list"
          tag="div"
          class="divide-y divide-zinc-100"
        >
          <article
            v-for="event in filteredEvents"
            :key="event.id"
            class="group grid gap-5 p-5 transition-all duration-500 hover:bg-zinc-50/70 lg:grid-cols-[190px_minmax(0,1fr)_auto] lg:items-center"
            :class="String(event.id) === recentlyClonedEventId ? 'bg-emerald-50/80 ring-1 ring-inset ring-emerald-200' : ''"
          >
            <div class="relative aspect-video overflow-hidden rounded-xl bg-zinc-900">
              <div
                v-if="!loadedEventImages[String(event.id)]"
                class="stream-skeleton absolute inset-0 z-[1]"
              />
              <img
                :src="event.image"
                :alt="event.title"
                class="h-full w-full object-cover opacity-90 transition duration-300 group-hover:scale-[1.02]"
                @load="markEventImageLoaded(event.id)"
                @error="markEventImageLoaded(event.id)"
              />
              <span
                class="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
                :class="event.lifecycle === 'In-Progress'
                  ? 'bg-red-500'
                  : event.lifecycle === 'Drafts'
                    ? 'bg-zinc-700'
                    : event.lifecycle === 'Completed'
                      ? 'bg-emerald-600'
                      : event.lifecycle === 'Past'
                        ? 'bg-amber-600'
                        : 'bg-blue-600'"
              >
                {{ event.lifecycle === 'Drafts' ? 'Draft' : event.lifecycle }}
              </span>
            </div>

            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <p class="text-xs font-bold uppercase tracking-[0.1em] text-blue-600">{{ selectedProduct?.name }}</p>
                <span class="h-1 w-1 rounded-full bg-zinc-300"></span>
                <p class="text-xs font-medium text-zinc-500">{{ event.format }}</p>
              </div>
              <h2 class="mt-1.5 truncate text-lg font-bold text-zinc-950">{{ event.title }}</h2>
              <div class="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-500">
                <span class="inline-flex items-center gap-1.5"><CalendarDaysIcon class="h-4 w-4" />{{ event.dateLabel }}</span>
                <span class="inline-flex items-center gap-1.5"><ClockIcon class="h-4 w-4" />{{ event.timeLabel }}</span>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-md text-blue-600 underline-offset-4 hover:text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  @click="openRegistrants(event)"
                >
                  <UsersIcon class="h-4 w-4" />{{ event.registrations }} registered
                </button>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 lg:justify-end">
              <button
                v-if="event.lifecycle !== 'Completed'"
                type="button"
                class="inline-flex items-center gap-2 rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-800 shadow-sm hover:bg-zinc-50"
                @click="openEditWizard(event)"
              >
                <PencilSquareIcon class="h-4 w-4" />
                Edit
              </button>
              <button
                v-if="!['Past', 'Completed'].includes(event.lifecycle)"
                type="button"
                class="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-zinc-800"
                @click="openStudio(event)"
              >
                <PlayIcon class="h-4 w-4" />
                Enter studio
              </button>
              <button
                v-if="event.lifecycle === 'Completed'"
                type="button"
                class="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-zinc-800"
                @click="openReplay(event)"
              >
                <PlayIcon class="h-4 w-4" />
                Watch replay
              </button>
              <button
                type="button"
                class="rounded-xl p-2.5 text-zinc-500 transition hover:bg-blue-50 hover:text-blue-600 disabled:cursor-wait disabled:opacity-50"
                :disabled="Boolean(cloningEventIds[String(event.id)])"
                aria-label="Clone webinar"
                title="Clone webinar"
                @click="cloneWebinarEvent(event)"
              >
                <ArrowPathIcon v-if="cloningEventIds[String(event.id)]" class="h-5 w-5 animate-spin" />
                <DocumentDuplicateIcon v-else class="h-5 w-5" />
              </button>
              <button
                type="button"
                class="rounded-xl p-2.5 text-zinc-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-wait disabled:opacity-50"
                :disabled="Boolean(webinarStore.deletingEventIds[String(event.id)])"
                aria-label="Delete event"
                @click="deleteWebinarEvent(event)"
              >
                <TrashIcon class="h-5 w-5" />
              </button>
            </div>
          </article>
        </TransitionGroup>

        <div v-else class="flex flex-col items-center px-6 py-16 text-center">
          <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50">
            <CalendarDaysIcon class="h-8 w-8 text-blue-600" />
          </div>
          <h2 class="mt-5 text-lg font-bold text-zinc-900">No {{ activeTab.toLowerCase() }} events</h2>
          <p class="mt-2 max-w-md text-sm leading-6 text-zinc-500">
            Create an event for this Webinar product and it will appear here.
          </p>
          <button type="button" class="mt-5 text-sm font-semibold text-blue-600 hover:text-blue-700" @click="openCreateWizard">
            Create your first event →
          </button>
        </div>
      </section>
    </template>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  ArrowPathIcon,
  ArrowUpRightIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  PencilSquareIcon,
  PlayIcon,
  PlusIcon,
  SignalIcon,
  SparklesIcon,
  TrashIcon,
  UsersIcon,
  VideoCameraIcon,
} from '@heroicons/vue/24/outline';
import { useFeatureStore } from '@/stores/featureStore';
import { useWebinarStore } from '@/stores/webinarStore';
import { useWebinarLiveStore } from '@/stores/webinarLiveStore';
import WebinarEventWizard from '@/Components/WebinarEventWizard.vue';
import WebinarRegistrantsModal from '@/Components/WebinarRegistrantsModal.vue';
import WebinarReplayPlayer from '@/Components/WebinarReplayPlayer.vue';
import WebinarStreamStudio from '@/Components/WebinarStreamStudio.vue';
import webinarImage from '@/assets/media/images/webinar.png';

const featureStore = useFeatureStore();
const webinarStore = useWebinarStore();
const webinarLiveStore = useWebinarLiveStore();
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');
const isLoadingProducts = ref(true);
const saveError = ref('');
const eventLoadError = ref('');
const previewMode = ref(false);
const selectedProductId = ref('');
const activeTab = ref('Upcoming');
const activeView = ref('hub');
const editingEvent = ref(null);
const studioEvent = ref(null);
const replayEvent = ref(null);
const registrantsEvent = ref(null);
const loadedEventImages = ref({});
const cloningEventIds = ref({});
const recentlyClonedEventId = ref('');
const cloneSuccessMessage = ref('');
let cloneFeedbackTimer = null;
let lifecycleTimer = null;
let replayPollTimer = null;
let registrationRefreshTimer = null;
const lifecycleNowMs = ref(Date.now());

const emptyStateSteps = [
  { step: 1, label: 'Add the product', copy: 'Choose Webinar inside Theme Editor.' },
  { step: 2, label: 'Design the page', copy: 'Style registration, waiting, and replay views.' },
  { step: 3, label: 'Run it here', copy: 'Schedule events and launch from Engage → Stream.' },
];

const prototypeProduct = {
  id: 'prototype-webinar',
  name: 'Weekly Member Webinar',
  slug: 'weekly-member-webinar',
  isPrototype: true,
};

const prototypeEvents = ref([
  {
    id: 'event-101',
    productId: prototypeProduct.id,
    title: 'The 5 Steps to Building a 7-Figure Membership',
    description: 'A private live workshop for members with Q&A at the end.',
    date: '2026-08-20',
    time: '19:00',
    timezone: 'America/New_York',
    dateLabel: 'Thu, Aug 20, 2026',
    timeLabel: '7:00 PM EDT',
    registrations: 148,
    status: 'Scheduled',
    format: 'Webcam + screen share',
    source: 'browser',
    image: webinarImage,
    registrationRequired: true,
    enableChat: true,
    recordReplay: true,
  },
  {
    id: 'event-102',
    productId: prototypeProduct.id,
    title: 'Creator Office Hours: Live Q&A',
    description: 'Monthly questions, coaching, and member spotlights.',
    date: '2026-08-28',
    time: '13:00',
    timezone: 'America/New_York',
    dateLabel: 'Fri, Aug 28, 2026',
    timeLabel: '1:00 PM EDT',
    registrations: 62,
    status: 'Scheduled',
    format: 'OBS / streaming software',
    source: 'obs',
    image: webinarImage,
    registrationRequired: true,
    enableChat: true,
    recordReplay: true,
  },
  {
    id: 'event-103',
    productId: prototypeProduct.id,
    title: 'September Product Reveal',
    description: '',
    date: '2026-09-03',
    time: '18:30',
    timezone: 'America/New_York',
    dateLabel: 'Thu, Sep 3, 2026',
    timeLabel: '6:30 PM EDT',
    registrations: 0,
    status: 'Draft',
    format: 'Broadcast source not selected',
    source: '',
    image: webinarImage,
    registrationRequired: true,
    enableChat: true,
    recordReplay: true,
  },
  {
    id: 'event-099',
    productId: prototypeProduct.id,
    title: 'July Member Growth Workshop',
    description: 'Replay from the July member workshop.',
    date: '2026-07-16',
    time: '19:00',
    timezone: 'America/New_York',
    dateLabel: 'Thu, Jul 16, 2026',
    timeLabel: '7:00 PM EDT',
    registrations: 203,
    status: 'Completed',
    apiStatus: 'ended',
    startedAt: '2026-07-16T23:00:00.000Z',
    endedAt: '2026-07-17T00:12:00.000Z',
    format: 'Replay available',
    source: 'browser',
    image: webinarImage,
    registrationRequired: true,
    enableChat: true,
    recordReplay: true,
  },
]);

const normalizeFeatureId = (feature) => String(
  feature?.instance_id ?? feature?.feature_instance_id ?? feature?.id ?? ''
);

const realWebinarProducts = computed(() => {
  const rows = Array.isArray(featureStore?.state?.featureInstances)
    ? featureStore.state.featureInstances
    : [];

  return rows
    .filter((feature) => {
      const name = String(feature?.feature_name || feature?.name || '').trim().toLowerCase();
      const slug = String(feature?.slug || '').trim().toLowerCase();
      return name === 'webinar' || name.includes('webinar') || slug.includes('webinar');
    })
    .map((feature) => ({
      id: normalizeFeatureId(feature),
      name: String(feature?.custom_name || feature?.feature_name || feature?.slug || 'Webinar'),
      slug: String(feature?.slug || ''),
      raw: feature,
      isPrototype: false,
    }))
    .filter((feature) => feature.id);
});

const hasRealWebinarProduct = computed(() => realWebinarProducts.value.length > 0);
const availableProducts = computed(() => (
  hasRealWebinarProduct.value ? realWebinarProducts.value : (previewMode.value ? [prototypeProduct] : [])
));
const hasWebinarProduct = computed(() => availableProducts.value.length > 0);
const selectedProduct = computed(() => (
  availableProducts.value.find((product) => String(product.id) === String(selectedProductId.value))
  || availableProducts.value[0]
  || null
));

function dateTimeParts(isoValue, timezone) {
  if (!isoValue) return { date: '', time: '' };
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

function mapApiEvent(event) {
  const timezone = event.scheduledTimezone || 'America/New_York';
  const local = dateTimeParts(event.scheduledStartAt, timezone);
  const statusMap = {
    draft: 'Draft', scheduled: 'Scheduled', ready: 'Ready', live: 'Live', ended: 'Completed', cancelled: 'Past',
  };
  const apiStatus = String(event.status || 'draft').toLowerCase();
  const scheduledDate = event.scheduledStartAt ? new Date(event.scheduledStartAt) : null;
  return {
    id: String(event.eventId),
    eventUuid: event.eventUuid,
    productId: String(event.featureInstanceId),
    title: event.title,
    description: event.description || '',
    bulletPoints: Array.isArray(event.bulletPoints) ? event.bulletPoints : [],
    hostName: event.hostName || '',
    hostAvatarUrl: event.hostAvatarUrl || '',
    hostAvatarMediaFileId: event.hostAvatarMediaFileId || '',
    introVideoUrl: event.introVideoUrl || '',
    introVideoMediaFileId: event.introVideoMediaFileId || '',
    coverImageUrl: event.coverImageUrl || '',
    coverMediaFileId: event.coverMediaFileId || '',
    image: event.coverMediaFileId
      ? `${apiProxyUrl}/api/image/${encodeURIComponent(event.coverMediaFileId)}`
      : (event.coverImageUrl || webinarImage),
    startMode: event.startMode || 'scheduled',
    scheduledStartAt: event.scheduledStartAt || null,
    startedAt: event.startedAt || null,
    endedAt: event.endedAt || null,
    createdAt: event.createdAt || null,
    updatedAt: event.updatedAt || null,
    date: local.date,
    time: local.time,
    timezone,
    dateLabel: event.startMode === 'go_live_now'
      ? 'Go live now'
      : (scheduledDate && !Number.isNaN(scheduledDate.getTime())
        ? new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(scheduledDate)
        : 'Date not set'),
    timeLabel: event.startMode === 'go_live_now'
      ? 'Ready to enter studio'
      : (scheduledDate && !Number.isNaN(scheduledDate.getTime())
        ? new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(scheduledDate)
        : 'Time not set'),
    registrations: Number(event.registrationCount ?? event.metadata?.registrationCount ?? 0),
    showUpCount: Number(event.showUpCount || 0),
    conversionRate: Number(event.conversionRate || 0),
    totalWatchSeconds: Number(event.totalWatchSeconds || 0),
    averageWatchSeconds: Number(event.averageWatchSeconds || 0),
    status: statusMap[apiStatus] || 'Draft',
    apiStatus,
    format: event.broadcastSource === 'obs' ? 'OBS / streaming software' : 'Webcam + screen share',
    source: event.broadcastSource || 'browser',
    registrationRequired: event.registrationRequired !== false,
    sendReminders: event.sendReminders !== false,
    waitingRoomMinutes: Number(event.waitingRoomMinutes || 15),
    enableChat: event.enableChat !== false,
    enableReactions: event.enableReactions !== false,
    recordReplay: event.recordReplay !== false,
    metadata: event.metadata && typeof event.metadata === 'object' ? event.metadata : {},
    replayUrl: event.replayUrl || event.metadata?.replayUrl || event.metadata?.replayMp4Url || '',
    replayDownloadUrl: event.replayDownloadUrl || event.metadata?.replayDownloadUrl || '',
  };
}

const isLoadingEvents = computed(() => (
  Boolean(selectedProduct.value && !selectedProduct.value.isPrototype)
  && webinarStore.isLoadingProduct(selectedProductId.value)
));

const eventsForProduct = computed(() => {
  if (selectedProduct.value?.isPrototype) return prototypeEvents.value;
  return webinarStore.eventsForProduct(selectedProductId.value).map(mapApiEvent);
});

function eventLifecycle(event) {
  const status = String(event.apiStatus || event.status || '').trim().toLowerCase();
  if (status === 'live' || status === 'in-progress') return 'In-Progress';
  if (status === 'draft' || status === 'drafts') return 'Drafts';
  if (status === 'ended' || status === 'completed') {
    return event.startedAt ? 'Completed' : 'Past';
  }
  if (status === 'cancelled' || status === 'past') return 'Past';

  if (status === 'scheduled' || status === 'ready') {
    if (String(event.startMode || '').toLowerCase() === 'go_live_now') return 'Upcoming';
    const scheduledAt = scheduledEventTimestamp(event);
    if (scheduledAt > 0 && lifecycleNowMs.value > scheduledAt + (15 * 60 * 1000)) return 'Past';
    return 'Upcoming';
  }

  return 'Drafts';
}

function scheduledEventTimestamp(event) {
  if (event.scheduledStartAt) {
    const parsed = new Date(event.scheduledStartAt).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  if (event.date) {
    const parsed = new Date(`${event.date}T${event.time || '00:00'}:00`).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

const categorizedEvents = computed(() => eventsForProduct.value.map((event) => ({
  ...event,
  lifecycle: eventLifecycle(event),
})));

const eventTabs = computed(() => [
  { id: 'In-Progress', label: 'In-Progress', count: categorizedEvents.value.filter((event) => event.lifecycle === 'In-Progress').length },
  { id: 'Drafts', label: 'Drafts', count: categorizedEvents.value.filter((event) => event.lifecycle === 'Drafts').length },
  { id: 'Upcoming', label: 'Upcoming', count: categorizedEvents.value.filter((event) => event.lifecycle === 'Upcoming').length },
  { id: 'Past', label: 'Past', count: categorizedEvents.value.filter((event) => event.lifecycle === 'Past').length },
  { id: 'Completed', label: 'Completed', count: categorizedEvents.value.filter((event) => event.lifecycle === 'Completed').length },
]);

function eventTimestamp(event) {
  const stored = event.scheduledStartAt || event.updatedAt || event.createdAt;
  if (stored) {
    const parsed = new Date(stored).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  if (event.date) {
    const parsed = new Date(`${event.date}T${event.time || '00:00'}:00`).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

const filteredEvents = computed(() => {
  const rows = categorizedEvents.value.filter((event) => event.lifecycle === activeTab.value);
  return [...rows].sort((left, right) => {
    if (activeTab.value === 'Upcoming') {
      const priority = { Ready: 0, Scheduled: 1 };
      const priorityDifference = (priority[left.status] ?? 2) - (priority[right.status] ?? 2);
      if (priorityDifference) return priorityDifference;
      return eventTimestamp(left) - eventTimestamp(right);
    }
    return eventTimestamp(right) - eventTimestamp(left);
  });
});

const metrics = computed(() => {
  const upcoming = categorizedEvents.value.filter((event) => event.lifecycle === 'Upcoming');
  const registrations = upcoming.reduce((total, event) => total + Number(event.registrations || 0), 0);
  const inProgress = categorizedEvents.value.filter((event) => event.lifecycle === 'In-Progress');
  return [
    { label: 'Upcoming events', value: upcoming.length, note: 'For the selected Webinar product', icon: CalendarDaysIcon, iconClass: 'text-blue-600' },
    { label: 'Total registrations', value: registrations.toLocaleString(), note: selectedProduct.value?.isPrototype ? 'Prototype registration count' : 'For upcoming events', icon: UsersIcon, iconClass: 'text-violet-600' },
    { label: 'Live right now', value: inProgress.length, note: inProgress.length ? 'Broadcasting to members' : 'No active broadcast', icon: SignalIcon, iconClass: 'text-emerald-600' },
  ];
});

watch(availableProducts, (products) => {
  if (!products.length) {
    selectedProductId.value = '';
    return;
  }
  if (!products.some((product) => String(product.id) === String(selectedProductId.value))) {
    selectedProductId.value = String(products[0].id);
  }
}, { immediate: true });

watch(selectedProductId, async (productId) => {
  eventLoadError.value = '';
  loadedEventImages.value = {};
  const product = availableProducts.value.find((row) => String(row.id) === String(productId));
  if (!productId || !product || product.isPrototype) return;
  try {
    await webinarStore.fetchEvents(productId);
  } catch (error) {
    eventLoadError.value = String(error?.message || 'Unable to load Webinar events.');
  }
}, { immediate: true });

function markEventImageLoaded(eventId) {
  const key = String(eventId || '');
  if (!key || loadedEventImages.value[key]) return;
  loadedEventImages.value = { ...loadedEventImages.value, [key]: true };
}

function openCreateWizard() {
  saveError.value = '';
  editingEvent.value = null;
  activeView.value = 'event-wizard';
}

function openEditWizard(event) {
  saveError.value = '';
  editingEvent.value = event ? { hostName: 'Platforma Creator', startMode: 'scheduled', ...event } : null;
  activeView.value = 'event-wizard';
}

function openStudio(event) {
  studioEvent.value = event ? { ...event } : null;
  activeView.value = 'studio';
}

function openRegistrants(event) {
  registrantsEvent.value = event ? { ...event, isPrototype: Boolean(selectedProduct.value?.isPrototype) } : null;
}

async function loadReplay(event) {
  if (!event || selectedProduct.value?.isPrototype) return;
  try {
    const replay = await webinarLiveStore.fetchReplay(event.id);
    if (!replayEvent.value || String(replayEvent.value.id) !== String(event.id)) return;
    replayEvent.value = {
      ...replayEvent.value,
      replayLoading: false,
      replayStatus: replay?.status || '',
      replayUrl: replay?.playbackUrl || '',
      replayDownloadUrl: replay?.downloadUrl || '',
      replayError: replay?.failureMessage || '',
      replayRecording: replay || null,
    };
    if (['starting', 'recording', 'finalizing', 'processing'].includes(String(replay?.status || '').toLowerCase())) {
      replayPollTimer = window.setTimeout(() => loadReplay(event), 5000);
    }
  } catch (error) {
    if (!replayEvent.value || String(replayEvent.value.id) !== String(event.id)) return;
    replayEvent.value = {
      ...replayEvent.value,
      replayLoading: false,
      replayStatus: 'failed',
      replayError: String(error?.message || 'Unable to load the Webinar replay.'),
    };
  }
}

function openReplay(event) {
  if (replayPollTimer) window.clearTimeout(replayPollTimer);
  replayEvent.value = event ? { ...event, replayLoading: !selectedProduct.value?.isPrototype } : null;
  if (event && !selectedProduct.value?.isPrototype) void loadReplay(event);
}

function closeReplay() {
  if (replayPollTimer) window.clearTimeout(replayPollTimer);
  replayPollTimer = null;
  replayEvent.value = null;
}

function returnToHub() {
  const shouldRefreshEvents = activeView.value === 'studio'
    && selectedProductId.value
    && !selectedProduct.value?.isPrototype;
  activeView.value = 'hub';
  editingEvent.value = null;
  studioEvent.value = null;
  if (shouldRefreshEvents) {
    webinarStore.fetchEvents(selectedProductId.value, { force: true }).catch((error) => {
      eventLoadError.value = String(error?.message || 'Unable to refresh Webinar events.');
    });
  }
}

function formatPrototypeDate(dateValue) {
  if (!dateValue) return 'Date not set';
  const parsed = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(parsed);
}

function formatPrototypeTime(timeValue) {
  if (!timeValue) return 'Time not set';
  const [hours = 0, minutes = 0] = String(timeValue).split(':').map(Number);
  const parsed = new Date();
  parsed.setHours(hours, minutes, 0, 0);
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(parsed);
}

function localDateTimeToUtc(dateValue, timeValue, timezone) {
  if (!dateValue || !timeValue) return null;
  const [year, month, day] = dateValue.split('-').map(Number);
  const [hour, minute] = timeValue.split(':').map(Number);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;

  const targetWallTime = Date.UTC(year, month - 1, day, hour, minute, 0);
  let utcGuess = targetWallTime;
  for (let pass = 0; pass < 3; pass += 1) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(utcGuess)).reduce((result, part) => ({ ...result, [part.type]: Number(part.value) }), {});
    const renderedWallTime = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    utcGuess += targetWallTime - renderedWallTime;
  }
  return new Date(utcGuess).toISOString();
}

function toApiPayload(payload) {
  const isGoLiveNow = payload.startMode === 'go_live_now';
  return {
    featureInstanceId: Number(payload.productId || selectedProductId.value),
    title: payload.title,
    description: payload.description || null,
    bulletPoints: Array.isArray(payload.bulletPoints)
      ? payload.bulletPoints.map((bullet) => String(bullet || '').trim()).filter(Boolean).slice(0, 4)
      : [],
    hostName: payload.hostName,
    hostAvatarMediaFileId: payload.hostAvatarMediaFileId || null,
    hostAvatarUrl: payload.hostAvatarUrl || null,
    introVideoMediaFileId: payload.introVideoMediaFileId || null,
    introVideoUrl: payload.introVideoUrl || null,
    coverMediaFileId: payload.coverMediaFileId || null,
    coverImageUrl: payload.coverImageUrl || null,
    startMode: payload.startMode || 'scheduled',
    scheduledStartAt: isGoLiveNow ? null : localDateTimeToUtc(payload.date, payload.time, payload.timezone),
    scheduledTimezone: payload.timezone || 'America/New_York',
    waitingRoomMinutes: Number(payload.waitingRoomMinutes || 15),
    broadcastSource: payload.source || 'browser',
    status: String(payload.status || 'scheduled').toLowerCase(),
    registrationRequired: payload.registrationRequired !== false,
    sendReminders: payload.sendReminders !== false,
    enableChat: payload.enableChat !== false,
    enableReactions: payload.enableReactions !== false,
    recordReplay: payload.recordReplay !== false,
  };
}

function savePrototypeEvent(payload) {
  const existingIndex = prototypeEvents.value.findIndex((event) => event.id === payload.id);
  const nextEvent = {
    ...payload,
    id: payload.id || `event-${Date.now()}`,
    productId: String(payload.productId || selectedProductId.value),
    dateLabel: payload.startMode === 'go_live_now' ? 'Go live now' : formatPrototypeDate(payload.date),
    timeLabel: payload.startMode === 'go_live_now'
      ? 'Ready to enter studio'
      : `${formatPrototypeTime(payload.time)} ${payload.timezone === 'America/New_York' ? 'ET' : ''}`.trim(),
    registrations: Number(payload.registrations || 0),
    status: payload.status || 'Scheduled',
    apiStatus: String(payload.status || 'scheduled').toLowerCase(),
    format: payload.source === 'obs' ? 'OBS / streaming software' : 'Webcam + screen share',
    image: payload.image || webinarImage,
  };

  if (existingIndex >= 0) {
    prototypeEvents.value.splice(existingIndex, 1, nextEvent);
  } else {
    prototypeEvents.value.unshift(nextEvent);
  }
  selectedProductId.value = nextEvent.productId;
  activeTab.value = nextEvent.status === 'Draft' ? 'Drafts' : 'Upcoming';
  returnToHub();
}

async function saveWebinarEvent(payload) {
  saveError.value = '';
  if (selectedProduct.value?.isPrototype) {
    savePrototypeEvent(payload);
    return;
  }

  try {
    const input = toApiPayload(payload);
    const saved = payload.id
      ? await webinarStore.updateEvent(payload.id, input)
      : await webinarStore.createEvent(input);
    const mapped = mapApiEvent(saved);
    if (payload.enableModeratorAccess) {
      try {
        const currentModerator = await webinarLiveStore.fetchModeratorAccess(mapped.id);
        if (!currentModerator?.access) {
          await webinarLiveStore.generateModeratorAccess(mapped.id, {
            displayName: String(payload.moderatorDisplayName || 'Moderator'),
            avatarMediaFileId: payload.moderatorAvatarMediaFileId || null,
          });
        }
      } catch (moderatorError) {
        eventLoadError.value = `The event was saved, but its moderator link could not be generated: ${String(moderatorError?.message || 'Open the Studio Moderator tab to try again.')}`;
      }
    }
    selectedProductId.value = String(mapped.productId);
    if (mapped.startMode === 'go_live_now') {
      openStudio(mapped);
      return;
    }
    activeTab.value = mapped.status === 'Draft' ? 'Drafts' : 'Upcoming';
    returnToHub();
  } catch (error) {
    saveError.value = String(error?.message || 'Unable to save this Webinar event.');
  }
}

function nextCloneTitle(title) {
  const original = String(title || 'Untitled webinar').trim() || 'Untitled webinar';
  const base = original.replace(/\s+\(Copy(?:\s+\d+)?\)$/i, '').trim() || 'Untitled webinar';
  const existingTitles = new Set(eventsForProduct.value.map((event) => String(event.title || '').trim().toLowerCase()));
  let copyNumber = 1;
  let candidate = `${base} (Copy)`;
  while (existingTitles.has(candidate.toLowerCase())) {
    copyNumber += 1;
    candidate = `${base} (Copy ${copyNumber})`;
  }
  return candidate;
}

function showCloneSuccess(event) {
  if (cloneFeedbackTimer) clearTimeout(cloneFeedbackTimer);
  recentlyClonedEventId.value = String(event.id || '');
  cloneSuccessMessage.value = `“${event.title}” is a separate draft and is ready to edit.`;
  cloneFeedbackTimer = setTimeout(() => {
    recentlyClonedEventId.value = '';
    cloneSuccessMessage.value = '';
  }, 4200);
}

async function cloneWebinarEvent(event) {
  if (!event?.id || cloningEventIds.value[String(event.id)]) return;
  const eventKey = String(event.id);
  cloningEventIds.value = { ...cloningEventIds.value, [eventKey]: true };
  eventLoadError.value = '';

  try {
    const cloneTitle = nextCloneTitle(event.title);
    if (selectedProduct.value?.isPrototype) {
      const clonedEvent = {
        ...event,
        id: `event-${Date.now()}`,
        eventUuid: '',
        title: cloneTitle,
        status: 'Draft',
        apiStatus: 'draft',
        registrations: 0,
        bulletPoints: Array.isArray(event.bulletPoints) ? [...event.bulletPoints] : [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      prototypeEvents.value = [clonedEvent, ...prototypeEvents.value];
      activeTab.value = 'Drafts';
      showCloneSuccess(clonedEvent);
      return;
    }

    const cloneInput = toApiPayload({
      ...event,
      id: null,
      title: cloneTitle,
      status: 'Draft',
      registrations: 0,
      bulletPoints: Array.isArray(event.bulletPoints) ? [...event.bulletPoints] : [],
    });
    const saved = await webinarStore.createEvent(cloneInput);
    const clonedEvent = mapApiEvent(saved);
    activeTab.value = 'Drafts';
    showCloneSuccess(clonedEvent);
  } catch (error) {
    eventLoadError.value = String(error?.message || 'Unable to clone this Webinar event.');
  } finally {
    cloningEventIds.value = { ...cloningEventIds.value, [eventKey]: false };
  }
}

async function deleteWebinarEvent(event) {
  if (!event) return;
  if (!window.confirm(`Delete “${event.title}”? This removes it from the creator event list.`)) return;

  eventLoadError.value = '';
  if (selectedProduct.value?.isPrototype) {
    prototypeEvents.value = prototypeEvents.value.filter((row) => String(row.id) !== String(event.id));
    return;
  }

  try {
    await webinarStore.deleteEvent(event.id, event.productId || selectedProductId.value);
  } catch (error) {
    eventLoadError.value = String(error?.message || 'Unable to delete this Webinar event.');
  }
}

onMounted(async () => {
  lifecycleTimer = window.setInterval(() => {
    lifecycleNowMs.value = Date.now();
  }, 30000);
  registrationRefreshTimer = window.setInterval(() => {
    if (activeView.value !== 'hub' || !selectedProductId.value || selectedProduct.value?.isPrototype) return;
    webinarStore.fetchEvents(selectedProductId.value, { force: true, silent: true }).catch(() => {});
  }, 20000);
  try {
    if (!featureStore.state.featureInstances.length) {
      await featureStore.fetchFeatureInstances();
    }
  } finally {
    isLoadingProducts.value = false;
  }
});

onBeforeUnmount(() => {
  if (cloneFeedbackTimer) clearTimeout(cloneFeedbackTimer);
  if (lifecycleTimer) window.clearInterval(lifecycleTimer);
  if (replayPollTimer) window.clearTimeout(replayPollTimer);
  if (registrationRefreshTimer) window.clearInterval(registrationRefreshTimer);
});
</script>

<style scoped>
.webinar-event-list-enter-active,
.webinar-event-list-leave-active,
.webinar-event-list-move {
  transition: opacity 320ms ease, transform 320ms ease;
}

.webinar-event-list-enter-from {
  opacity: 0;
  transform: translateY(-14px) scale(0.985);
}

.webinar-event-list-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.985);
}

.clone-toast-enter-active,
.clone-toast-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.clone-toast-enter-from,
.clone-toast-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.97);
}

.stream-skeleton {
  position: relative;
  overflow: hidden;
  background: #e4e4e7;
}

.stream-skeleton::after {
  position: absolute;
  inset: 0;
  content: '';
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgb(255 255 255 / 74%), transparent);
  animation: stream-skeleton-shimmer 1.15s ease-in-out infinite;
}

@keyframes stream-skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
